// src/components/ImageSettingsPanel.js
// Admin can toggle image sharing on/off and see daily usage
import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function ImageSettingsPanel({ toast }) {
  const [settings, setSettings] = useState({
    enabled: true,
    gold_daily_limit: 3,
    premium_daily_limit: 7,
    platinum_daily_limit: 10
  })
  const [saving, setSaving] = useState(false)
  const [usage, setUsage]   = useState([])

  useEffect(() => { loadSettings(); loadUsage() }, [])

  async function loadSettings() {
    const { data } = await supabase
      .from('image_settings').select('*').eq('id', 1).single()
    if (data) setSettings(data)
  }

  async function loadUsage() {
    const today = new Date().toISOString().split('T')[0]
    const { data } = await supabase
      .from('image_usage')
      .select('*, profiles(display_name, tier)')
      .eq('usage_date', today)
      .order('count', { ascending: false })
    if (data) setUsage(data)
  }

  async function handleSave() {
    setSaving(true)
    const { error } = await supabase
      .from('image_settings')
      .update({ ...settings, updated_at: new Date().toISOString() })
      .eq('id', 1)
    setSaving(false)
    if (error) { toast.show('Failed to save', 'error'); return }
    toast.show('Image settings saved', 'success')
  }

  async function toggleImages() {
    const newEnabled = !settings.enabled
    setSettings(s => ({ ...s, enabled: newEnabled }))
    await supabase.from('image_settings')
      .update({ enabled: newEnabled }).eq('id', 1)
    toast.show(newEnabled ? 'Image sharing enabled' : 'Image sharing disabled', 'success')
  }

  return (
    <div style={{ padding: 24 }}>
      <h2 className="admin-feature-title">Image Settings</h2>
      <p className="admin-feature-sub">
        Control image sharing in chat. Daily limits are hidden from members — they only see a
        "limit reached" message without knowing the number.
      </p>

      {/* Master toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '18px 20px', background: 'var(--bg-card)',
        border: '0.5px solid var(--border)', marginBottom: 20
      }}>
        <div>
          <p style={{ fontSize: 14, color: 'var(--cream)', marginBottom: 4 }}>Image Sharing</p>
          <p style={{ fontSize: 12, color: 'var(--muted)' }}>
            {settings.enabled ? 'Members can currently send images in chat' : 'Image sharing is currently disabled for all members'}
          </p>
        </div>
        <button
          className={`btn btn--sm ${settings.enabled ? 'btn--gold' : 'btn--ghost'}`}
          onClick={toggleImages}>
          {settings.enabled ? 'Enabled — Click to Disable' : 'Disabled — Click to Enable'}
        </button>
      </div>

      {/* Daily limits */}
      <div style={{ marginBottom: 24 }}>
        <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', marginBottom: 14 }}>
          Daily Limits (hidden from members)
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
          {[
            { key: 'gold_daily_limit', label: 'Gold', color: 'var(--gold)' },
            { key: 'premium_daily_limit', label: 'Premium', color: 'var(--plat)' },
            { key: 'platinum_daily_limit', label: 'Platinum', color: 'var(--plat-bright)' },
          ].map(({ key, label, color }) => (
            <div key={key} style={{
              background: 'var(--bg-card)', border: '0.5px solid var(--border)', padding: 16
            }}>
              <p style={{ fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color, marginBottom: 10 }}>
                {label}
              </p>
              <input
                type="number" min="0" max="50"
                value={settings[key]}
                onChange={e => setSettings(s => ({ ...s, [key]: parseInt(e.target.value) || 0 }))}
                style={{
                  width: '100%', background: 'var(--bg-input)', border: '0.5px solid var(--border)',
                  color: 'var(--cream)', padding: '8px 12px', fontSize: 18,
                  fontFamily: 'Cormorant Garamond, serif', fontWeight: 300, textAlign: 'center',
                  outline: 'none'
                }}
              />
              <p style={{ fontSize: 10, color: 'var(--dim)', textAlign: 'center', marginTop: 6 }}>
                images/day
              </p>
            </div>
          ))}
        </div>
        <button className="btn btn--gold" onClick={handleSave} disabled={saving} style={{ marginTop: 14 }}>
          {saving ? 'Saving...' : 'Save Limits'}
        </button>
      </div>

      {/* Today's usage */}
      <div>
        <p style={{ fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', color: 'var(--dim)', marginBottom: 14 }}>
          Today's Image Usage
        </p>
        {usage.length === 0 && (
          <p style={{ fontSize: 13, color: 'var(--dim)', padding: '16px 0' }}>No images sent today</p>
        )}
        {usage.map(u => (
          <div key={u.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 0', borderBottom: '0.5px solid rgba(255,255,255,0.04)'
          }}>
            <span style={{ fontSize: 13, color: 'var(--cream)' }}>
              {u.profiles?.display_name || 'Member'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>
              {u.profiles?.tier}
            </span>
            <span style={{ fontSize: 14, color: 'var(--gold)', fontFamily: 'Cormorant Garamond, serif' }}>
              {u.count} sent
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
